import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { GtfsProviderShellComponent } from './gtfs-provider-shell.component';
import { ProviderDetailsLayoutComponent } from '../shared/provider-details-layout.component';
import { LabelBadge, LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { Icon } from '../../../shared/components/icon/icon';
import { StackedBar, StackedBarSegment, StackedBarTone } from '../../../shared/components/stacked-bar/stacked-bar';
import { GtfsRealtimeState, GtfsStaticState } from '../../../shared/models/feed-diagnostics.model';
import { ProviderConfig } from '../models/provider.model';
import { LanguageService } from '../../../core/i18n/language.service';
import { relativeTime } from '../../../shared/utils/relative-time';
import { isSnapshotStale } from '../util/feed-diagnostics.util';
import { asGtfsDiagnostics, snapshotAgeMinutes } from '../util/gtfs-diagnostics.util';

/** Why a feed is on the "Needs attention" list, in list order. */
export type GtfsAttentionKind = 'ERROR' | 'STALE' | 'DEGRADED' | 'NONE';

const ATTENTION_ORDER: Record<GtfsAttentionKind, number> = { ERROR: 0, STALE: 1, DEGRADED: 2, NONE: 3 };
const ATTENTION_DOT: Record<GtfsAttentionKind, LabelBadgeDot | null> = {
  ERROR: 'red',
  STALE: 'red',
  DEGRADED: 'orange',
  NONE: null,
};

export interface GtfsAttentionRow {
  feed: ProviderConfig;
  tenantName: string;
  kind: GtfsAttentionKind;
  /** GTFS's message, or our own text for stale / no snapshot. */
  message: string;
  age: string | null;
}

/** Bar tone per state; "no snapshot" is counted as the unknown/none bucket. */
const STATIC_TONES: { key: GtfsStaticState; tone: StackedBarTone }[] = [
  { key: 'ACTIVE', tone: 'ok' },
  { key: 'IMPORTING', tone: 'warn' },
  { key: 'FAILED', tone: 'error' },
  { key: 'NONE', tone: 'unknown' },
];

const REALTIME_TONES: { key: GtfsRealtimeState; tone: StackedBarTone }[] = [
  { key: 'OK', tone: 'ok' },
  { key: 'IDLE', tone: 'warn' },
  { key: 'ERROR', tone: 'error' },
  { key: 'DISABLED', tone: 'off' },
  { key: 'UNKNOWN', tone: 'unknown' },
];

/**
 * GTFS Details tab (/admin/providers/gtfs/:providerId/details): the shared
 * provider Details layout (settings, Record, Feeds by tenant) with a GTFS Feed
 * health card in its slot — static-schedule and realtime `app-stacked-bar`s
 * plus a "Needs attention" list (Error → Stale → Degraded → No snapshot),
 * aggregated from the GTFS shell's one cross-tenant diagnostics map.
 *
 * GTFS diagnostics are per feed (only a `monitoredStops` count, no per-stop
 * status), so health is counted in feeds; a feed without a snapshot counts as
 * static None / realtime Unknown.
 */
@Component({
  selector: 'app-gtfs-provider-details',
  standalone: true,
  imports: [RouterLink, TranslocoDirective, LabelBadge, Icon, StackedBar, ProviderDetailsLayoutComponent],
  templateUrl: './gtfs-provider-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full min-h-0' },
})
export class GtfsProviderDetailsComponent {
  private readonly shell = inject(GtfsProviderShellComponent);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);

  readonly isDiagnosticsLoading = this.shell.isDiagnosticsLoading;
  readonly feeds = this.shell.feeds;
  readonly attentionDot = ATTENTION_DOT;

  readonly health = computed(() => {
    this.languageService.current(); // segment labels / messages re-translate on switch
    const t = (k: string, params?: Record<string, unknown>) => this.transloco.translate(`providers.detailPage.gtfsHealth.${k}`, params);
    const diagnostics = this.shell.diagnostics();
    const tenantNames = this.shell.tenantNameById();
    const staticCounts = new Map<string, number>();
    const realtimeCounts = new Map<string, number>();
    const bump = (m: Map<string, number>, key: string) => m.set(key, (m.get(key) ?? 0) + 1);
    const attention: GtfsAttentionRow[] = [];
    let newest: string | null = null;
    let oldest: string | null = null;

    for (const feed of this.shell.feeds()) {
      const snapshot = diagnostics.get(feed.id);
      const d = asGtfsDiagnostics(snapshot);
      const tenantName = tenantNames.get(feed.tenantId) ?? feed.tenantId;
      if (!snapshot || !d) {
        bump(staticCounts, 'NONE');
        bump(realtimeCounts, 'UNKNOWN');
        attention.push({ feed, tenantName, kind: 'NONE', message: t('noSnapshotMessage'), age: null });
        continue;
      }
      bump(staticCounts, d.static.state);
      bump(realtimeCounts, d.realtime.state);
      if (!newest || snapshot.generatedAt > newest) newest = snapshot.generatedAt;
      if (!oldest || snapshot.generatedAt < oldest) oldest = snapshot.generatedAt;

      const age = relativeTime(snapshot.generatedAt);
      if (d.status === 'ERROR') attention.push({ feed, tenantName, kind: 'ERROR', message: d.message, age });
      else if (isSnapshotStale(snapshot)) {
        attention.push({ feed, tenantName, kind: 'STALE', message: t('staleMessage', { minutes: snapshotAgeMinutes(snapshot) }), age });
      } else if (d.status === 'DEGRADED') attention.push({ feed, tenantName, kind: 'DEGRADED', message: d.message, age });
    }

    attention.sort((a, b) => ATTENTION_ORDER[a.kind] - ATTENTION_ORDER[b.kind] || a.feed.name.localeCompare(b.feed.name));
    const segments = (defs: { key: string; tone: StackedBarTone }[], group: string, counts: Map<string, number>): StackedBarSegment[] =>
      defs.map((def) => ({ label: t(`${group}.${def.key}`), count: counts.get(def.key) ?? 0, tone: def.tone }));

    return {
      staticSegments: segments(STATIC_TONES, 'staticState', staticCounts),
      realtimeSegments: segments(REALTIME_TONES, 'realtimeState', realtimeCounts),
      attention,
      newestAge: newest ? relativeTime(newest) : null,
      oldestAge: oldest ? relativeTime(oldest) : null,
    };
  });

  feedLink(feed: ProviderConfig): string[] {
    return [...this.shell.basePath(), feed.id];
  }

  addFeed(): void {
    this.shell.addFeed();
  }
}
